'use client';

import { amplitudeSendEvent, getScreenName, useDraggable } from '@musinsa/frontend-utils';
import {
  DiscountTypography,
  IcLikeFilled,
  Image,
  LikeMotion,
  PriceTypography,
  RankingLabel,
  Typography,
} from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import type { PopularProductListItem } from '@musinsa/services-curator';
import { ERROR_CODE_LIKE_UNAUTHORIZED, isApiError } from '@musinsa/services-curator';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FC, useState } from 'react';
import { createLoginUrl } from '../../common/auth/redirectToLogin';
import { useTrackingData } from '../../common/components/TrackingDataProvider';
import { useNavigate } from '../../common/hooks/useNavigate';
import { useProductLikeMutation } from '../../common/hooks/useProductLikeMutation';
import { productsFactory } from '../../query-factory/products.factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { useProductLinkNavigation } from '../hooks';

interface PopularProductsProps {
  snsTitle: string;
}

function popularItemToAmplitudeProduct(item: PopularProductListItem) {
  return {
    product_id: item.goodsNo,
    product_name: item.goodsName,
    price: item.price,
    discount_rate: item.salesRate,
    brand_name: item.brandName,
  };
}

interface PopularProductCardProps {
  item: PopularProductListItem;
  index: number;
  snsTitle: string;
}

const PopularProductCard: FC<PopularProductCardProps> = ({ item, index, snsTitle }) => {
  const { service, isMobile } = useDeviceInfo();
  const { navigate } = useNavigate();
  const navigateToProduct = useProductLinkNavigation();
  const { data: trackingData } = useTrackingData();
  const queryClient = useQueryClient();
  const [isLiked, setIsLiked] = useState(item.isLiked);

  const likeMutation = useProductLikeMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productsFactory.popular._def });
    },
    onError: (error: unknown, { isLiked: nextIsLiked }) => {
      if (isApiError(error) && error.data?.meta?.errorCode === ERROR_CODE_LIKE_UNAUTHORIZED) {
        const url = createLoginUrl(window.location.href);
        navigate(url);
        return;
      }
      setIsLiked(!nextIsLiked);
    },
  });

  const handleClick = () => {
    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_ITEM,
        data: {
          ...trackingData,
          item_no: Number(item.goodsNo),
          item_position: index + 1,
          position: index + 1,
          item_name: item.goodsName,
          brand_no: item.brandNo,
          brand_name: item.brandName,
        },
      });
    }
    if (service === SERVICE.MUSINSA) {
      amplitudeSendEvent({
        action_name: 'click',
        component_name: 'product',
        event_name: 'click_product',
        from: getScreenName(),
        id: snsTitle,
        index,
        section_name: 'curator_curationfeed',
        type: 'ranking',
        ...popularItemToAmplitudeProduct(item),
      });
    }
    if (item.linkUrl) {
      navigateToProduct(item.linkUrl);
    }
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !isLiked;
    setIsLiked(next);
    likeMutation.mutate({ goodsNo: String(item.goodsNo), isLiked: next });

    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.LIKE_ITEM,
        data: {
          ...trackingData,
          item_no: Number(item.goodsNo),
          item_position: index + 1,
          position: index + 1,
          item_name: item.goodsName,
          brand_no: item.brandNo,
          brand_name: item.brandName,
        },
      });
    }
    if (service === SERVICE.MUSINSA) {
      const product = popularItemToAmplitudeProduct(item);
      amplitudeSendEvent({
        action_name: next ? 'like_product' : 'unlike_product',
        component_name: 'product',
        event_name: next ? 'like_product_product' : 'unlike_product_product',
        from: getScreenName(),
        id: snsTitle,
        index,
        section_name: 'curator_curationfeed',
        type: 'ranking',
        ...product,
      });
    }
  };

  return (
    <div
      className="flex flex-col shrink-0 w-[109px] cursor-pointer"
      onClick={handleClick}
      role="button"
      tabIndex={0}
    >
      {/* 이미지 */}
      <div className="relative w-[109px] h-[131px] overflow-hidden bg-gray-100">
        <Image
          src={`${item.imageUrl}?width=${isMobile ? 250 : 400}&q=75&format=webp`}
          alt={item.goodsName}
          className="w-full h-full object-cover"
          loading={index === 0 ? 'eager' : 'lazy'}
        />
        <div className="absolute inset-0 bg-black/5" />
        {/* 랭킹 라벨 */}
        <div className="absolute top-2 left-2 z-10">
          <RankingLabel>{index + 1}</RankingLabel>
        </div>
        {/* 좋아요 */}
        <div className="absolute bottom-1 right-1 z-10">
          <LikeMotion like={isLiked}>
            {isLiked ? (
              <IcLikeFilled
                fillColor="red"
                strokeColor="transparent"
                width="20px"
                height="20px"
                onClick={handleLike}
              />
            ) : (
              <IcLikeFilled
                fillColor="gray-500"
                strokeColor="white"
                opacity={0.6}
                width="20px"
                height="20px"
                onClick={handleLike}
              />
            )}
          </LikeMotion>
        </div>
      </div>
      {/* 상품 정보 */}
      <div className="bg-white flex flex-col pt-2 pl-2 pr-1 pb-3 w-full">
        <div className="flex flex-col gap-0.5">
          <Typography variant="textEtc11pxSemibold" className="line-clamp-1">
            {item.brandName}
          </Typography>
          <Typography variant="body13pxReg" className="line-clamp-2 min-h-[36px]">
            {item.goodsName}
          </Typography>
        </div>
        <div className="inline-flex gap-0.5">
          {item.salesRate !== 0 && (
            <Typography variant="body13pxSemi" colorScheme="red">
              <DiscountTypography discount={item.salesRate} />
            </Typography>
          )}
          <Typography variant="body13pxSemi">
            {item.price > 0 ? <PriceTypography price={item.price} /> : '0원'}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export const PopularProducts: FC<PopularProductsProps> = ({ snsTitle }) => {
  const { data } = useQuery({
    ...productsFactory.popular({ snsTitle, size: 10 }),
    enabled: !!snsTitle,
  });
  const draggable = useDraggable<HTMLDivElement>();

  const items = data?.list ?? [];

  if (items.length === 0) {
    return null;
  }

  return (
    <section className="bg-gray-100 pb-3 w-full">
      <div className="flex items-center pt-3 pb-2 px-4">
        <Typography variant="body14pxMed">인기 상품</Typography>
      </div>
      <div ref={draggable.sliderRef} className="overflow-x-auto scrollbar-hide">
        <div className="inline-flex px-4">
          <div className="inline-flex rounded overflow-hidden">
            {items.map((item, index) => (
              <PopularProductCard
                key={item.goodsNo}
                item={item}
                index={index}
                snsTitle={snsTitle}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
